import {
  calculateAverage,
  calculateDeviation,
  formatDuration,
} from '../helpers/duration.helper';
import {
  AppTimeSeriesCubeMetric,
  CubeTimeDimensionGranularity,
} from '../enum/cube-schema.enum';
import { CumulativeAverageDurationResult } from '../dto/top-apps/cube/mapped/cumulative-avg-duration-result.dto';
import {
  TimeSeriesData,
  TimeSeriesResponseDto,
} from '../dto/common/response/time-series-response.dto';
import { AppMetricsResult } from '../dto/top-apps/cube/mapped/app-metrics-result.dto';

export const mapToAppTimeSeriesDetailedResults = (
  cumulativeAverages: CumulativeAverageDurationResult,
  cumulativeWeekdayAvg: CumulativeAverageDurationResult,
  appMetrics: AppMetricsResult,
  maxAppMetrics: AppMetricsResult,
  data: TimeSeriesData[],
  metric: AppTimeSeriesCubeMetric,
  granularity: CubeTimeDimensionGranularity
): TimeSeriesResponseDto => {
  let denominator;
  if (granularity === CubeTimeDimensionGranularity.Day) {
    denominator = cumulativeAverages.dayCount;
  } else {
    denominator = cumulativeAverages.weekCount;
  }
  let response: TimeSeriesResponseDto;
  if (metric === AppTimeSeriesCubeMetric.TimeInApp) {
    response = {
      data: data,
      metrics: {
        allTimeAverage: formatDuration(
          calculateAverage(cumulativeAverages.totalDuration, denominator)
        ),
        allTimeAverageLine: Math.round(
          calculateAverage(cumulativeAverages.totalDuration, denominator)
        ),
        weekdayAverage: formatDuration(
          calculateAverage(
            cumulativeWeekdayAvg.totalDuration,
            cumulativeWeekdayAvg.weekCount
          )
        ),
        max: formatDuration(maxAppMetrics.duration),
        value: formatDuration(appMetrics.duration),
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(appMetrics.duration),
            calculateAverage(cumulativeAverages.totalDuration, denominator)
          )
            .toFixed(1)
            .toString()}%` || '0',
      },
    };
  } else if (metric === AppTimeSeriesCubeMetric.Interactions) {
    response = {
      data: data,
      metrics: {
        allTimeAverage: calculateAverage(cumulativeAverages.count, denominator)
          .toFixed(0)
          .toString(),
        allTimeAverageLine: Math.round(
          calculateAverage(cumulativeAverages.count, denominator)
        ),
        weekdayAverage: calculateAverage(
          cumulativeWeekdayAvg.count,
          cumulativeWeekdayAvg.weekCount
        )
          .toFixed(0)
          .toString(),
        max: maxAppMetrics.count.toString(),
        value: appMetrics.count.toString() || '0',
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(appMetrics.count),
            calculateAverage(cumulativeAverages.count, denominator)
          )
            .toFixed(0)
            .toString()}%` || '0',
      },
    };
  } else if (metric === AppTimeSeriesCubeMetric.CallTimeInApp) {
    response = {
      data: data,
      metrics: {
        allTimeAverage: formatDuration(
          calculateAverage(cumulativeAverages.totalCallDuration, denominator)
        ),
        allTimeAverageLine: Math.round(
          calculateAverage(cumulativeAverages.totalCallDuration, denominator)
        ),
        weekdayAverage: formatDuration(
          calculateAverage(
            cumulativeWeekdayAvg.totalCallDuration,
            cumulativeWeekdayAvg.weekCount
          )
        ),
        max: formatDuration(maxAppMetrics.callDuration),
        value: formatDuration(appMetrics.callDuration),
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(appMetrics.callDuration),
            calculateAverage(cumulativeAverages.totalCallDuration, denominator)
          )
            .toFixed(1)
            .toString()}%` || '0',
      },
    };
  } else if (metric === AppTimeSeriesCubeMetric.CallInteractions) {
    response = {
      data: data,
      metrics: {
        allTimeAverage: calculateAverage(
          cumulativeAverages.callCount,
          denominator
        )
          .toFixed(0)
          .toString(),
        allTimeAverageLine: Math.round(
          calculateAverage(cumulativeAverages.callCount, denominator)
        ),
        weekdayAverage: calculateAverage(
          cumulativeWeekdayAvg.callCount,
          cumulativeWeekdayAvg.weekCount
        )
          .toFixed(0)
          .toString(),
        max: maxAppMetrics.callCount.toString(),
        value: appMetrics.callCount.toString() || '0',
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(appMetrics.callCount),
            calculateAverage(cumulativeAverages.callCount, denominator)
          )
            .toFixed(0)
            .toString()}%` || '0',
      },
    };
  } else if (metric === AppTimeSeriesCubeMetric.CallAvgDuration) {
    response = {
      data: data,
      metrics: {
        allTimeAverage: formatDuration(
          calculateAverage(
            cumulativeAverages.totalCallDuration,
            cumulativeAverages.callCount
          )
        ),
        allTimeAverageLine: Math.round(
          calculateAverage(
            cumulativeAverages.totalCallDuration,
            cumulativeAverages.callCount
          )
        ),
        weekdayAverage: formatDuration(
          calculateAverage(
            cumulativeWeekdayAvg.totalCallDuration,
            cumulativeWeekdayAvg.callCount
          )
        ),
        max: formatDuration(maxAppMetrics.callDurationPerCount),
        value: formatDuration(
          calculateAverage(appMetrics.callDuration, appMetrics.callCount)
        ),
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(
              calculateAverage(appMetrics.callDuration, appMetrics.callCount)
            ),
            calculateAverage(
              cumulativeAverages.totalCallDuration,
              cumulativeAverages.callCount
            )
          )
            .toFixed(1)
            .toString()}%` || '0',
      },
    };
  } else {
    response = {
      data: data,
      metrics: {
        allTimeAverage: formatDuration(
          calculateAverage(
            cumulativeAverages.totalDuration,
            cumulativeAverages.count
          )
        ),
        allTimeAverageLine: Math.round(
          calculateAverage(
            cumulativeAverages.totalDuration,
            cumulativeAverages.count
          )
        ),
        weekdayAverage: formatDuration(
          calculateAverage(
            cumulativeWeekdayAvg.totalDuration,
            cumulativeWeekdayAvg.count
          )
        ),
        max: formatDuration(maxAppMetrics.durationPerCount),
        value: formatDuration(
          calculateAverage(appMetrics.duration, appMetrics.count)
        ),
        deviationFromAveragePercentage:
          `${calculateDeviation(
            Number(calculateAverage(appMetrics.duration, appMetrics.count)),
            calculateAverage(
              cumulativeAverages.totalDuration,
              cumulativeAverages.count
            )
          )
            .toFixed(1)
            .toString()}%` || '0',
      },
    };
  }
  return response;
};
